import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

import { trackEvent } from '@/lib/analytics/trackEvent';
import { absoluteCheckoutTrackingUrl } from '@/lib/checkoutTrackingUrl';
import { openWhatsAppUrl, getBusinessWhatsappDigits } from '@/lib/checkoutWhatsAppOrder';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import CheckoutOrderThankYouHero from '@/pages/menu/checkout/CheckoutOrderThankYouHero';

export default function CheckoutOrderPlacedDialog({
  isDemoCheckout,
  orderPlacedOpen,
  onOrderPlacedOpenChange,
  primaryColor,
  businessId,
  business,
  routeBid,
  orderPlacedWhatsappUnavailable,
  orderPlacedOrderId,
  orderPlacedTotal,
  orderPlacedCopyText,
  orderPlacedTrackingUrl,
  orderPlacedWhatsappUrl,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Dialog
      open={orderPlacedOpen}
      onOpenChange={(open) => onOrderPlacedOpenChange?.(open)}
    >
      <DialogContent
        className="sm:max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="checkout-order-placed-dialog"
      >
        {!isDemoCheckout ? (
          <CheckoutOrderThankYouHero accentColor={primaryColor} className="pt-1 pb-2" />
        ) : null}

        <DialogHeader>
          <DialogTitle className="text-center">
            {isDemoCheckout ? t('checkout.orderPlacedDemo') : t('checkout.orderPlacedThankYouTitle')}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-left">
              {isDemoCheckout ? (
                <>
                  <p className="text-sm text-foreground">
                    Simulaste el flujo completo. Un cliente real enviaría esto por WhatsApp al local.
                  </p>
                  <div className="rounded-lg border border-violet-200/50 dark:border-violet-800/50 bg-violet-500/5 p-3 text-xs space-y-2 mt-2">
                    <p className="font-medium text-foreground">Estado demo</p>
                    <ul className="space-y-1 text-muted-foreground list-disc pl-4">
                      <li>Pedido recibido ✓</li>
                      <li>En preparación (simulado)</li>
                      <li>Listo (simulado)</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-foreground text-center leading-relaxed font-medium">
                    {t('checkout.orderPlacedWhatsAppHint')}
                  </p>
                  <p className="text-sm text-muted-foreground text-center leading-relaxed">
                    {t('checkout.orderPlacedWhatsAppConfirm')}
                  </p>
                </>
              )}

              {!isDemoCheckout && orderPlacedWhatsappUnavailable ? (
                <p className="text-sm text-amber-700 dark:text-amber-400 font-medium text-center">
                  {t('checkout.noWhatsAppConfigured')}
                </p>
              ) : !isDemoCheckout && orderPlacedWhatsappUrl ? (
                <p className="text-sm text-muted-foreground text-center">
                  {t('checkout.orderPlacedWhatsAppOpenedHint')}
                </p>
              ) : !isDemoCheckout ? (
                <p className="text-sm text-muted-foreground text-center">
                  {t('checkout.orderPlacedWhatsAppManualHint')}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Copiá el resumen de abajo para ver el formato del mensaje.
                </p>
              )}

              {orderPlacedOrderId ? (
                <p className="text-xs text-muted-foreground text-center">
                  Referencia: <span className="font-mono">{orderPlacedOrderId}</span>
                </p>
              ) : null}

              {orderPlacedTotal != null ? (
                <p className="text-sm font-medium tabular-nums text-center">
                  Total: ${orderPlacedTotal.toLocaleString('es-CL')}
                </p>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        {orderPlacedCopyText ? (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">{t('checkout.orderSummary')}</Label>
            <Textarea
              readOnly
              value={orderPlacedCopyText}
              className="min-h-[120px] max-h-[200px] text-xs font-mono resize-y"
            />
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          {!isDemoCheckout && orderPlacedTrackingUrl ? (
            <div
              className="rounded-lg border bg-muted/30 p-4 space-y-3"
              data-testid="checkout-order-tracking-block"
            >
              <h3 className="text-sm font-semibold text-foreground">{t('checkout.orderPlacedTracking')}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t('checkout.orderPlacedBody')}
              </p>
              <div className="flex flex-col gap-2">
                <Button asChild variant="default" className="w-full">
                  <Link
                    to={orderPlacedTrackingUrl}
                    state={{ fromCheckoutMenuBusinessId: routeBid }}
                    onClick={() => {
                      try {
                        trackEvent('checkout_order_tracking_opened', {});
                      } catch {
                        /* noop */
                      }
                    }}
                    data-testid="checkout-order-placed-tracking-open"
                  >
                    {t('checkout.viewTracking')}
                  </Link>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  data-testid="checkout-order-placed-tracking-copy"
                  onClick={async () => {
                    try {
                      const abs = absoluteCheckoutTrackingUrl(orderPlacedTrackingUrl);
                      await navigator.clipboard.writeText(abs);
                      toast.success(t('checkout.copyTrackingLink'));
                      try {
                        trackEvent('checkout_order_tracking_copied', {});
                      } catch {
                        /* noop */
                      }
                    } catch (e) {
                      console.warn('[checkout] clipboard tracking url failed', e);
                      toast.error(t('checkout.copyTrackingLink'));
                    }
                  }}
                >
                  <Copy className="w-4 h-4 shrink-0" />
                  {t('checkout.copyTrackingLink')}
                </Button>
              </div>
            </div>
          ) : null}

          {orderPlacedWhatsappUrl ? (
            <>
              <Button
                type="button"
                className="w-full text-white"
                style={{ backgroundColor: primaryColor }}
                onClick={() => {
                  const navigated = openWhatsAppUrl(orderPlacedWhatsappUrl, {
                    businessId,
                    source: 'menu_checkout',
                  });
                  if (!navigated) {
                    toast.error('No se pudo abrir WhatsApp. Probá “Copiar pedido”.');
                  }
                }}
                data-testid="checkout-order-placed-open-whatsapp"
              >
                {t('checkout.openWhatsApp')}
              </Button>
              {!isDemoCheckout && orderPlacedTrackingUrl ? (
                <p className="text-xs text-muted-foreground text-center px-1 leading-snug">
                  También podés copiar el enlace de seguimiento arriba o usar el que va en el mensaje de WhatsApp.
                </p>
              ) : null}
            </>
          ) : null}

          {orderPlacedCopyText ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(orderPlacedCopyText);
                  toast.success('Pedido copiado al portapapeles');
                } catch (e) {
                  console.warn('[checkout] clipboard write failed', e);
                  toast.error('No se pudo copiar. Seleccioná el texto manualmente.');
                }
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('checkout.copyOrder')}
            </Button>
          ) : null}

          {(() => {
            const digits = getBusinessWhatsappDigits(business);
            const telHref = digits.length >= 8 ? `tel:+${digits}` : null;
            if (telHref) {
              return (
                <Button type="button" variant="outline" className="w-full" asChild>
                  <a href={telHref}>{t('checkout.contactManually')}</a>
                </Button>
              );
            }
            return (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() =>
                  navigate(getPublicMenuPath(routeBid, 'home'), { state: { orderThankYou: true } })
                }
              >
                {t('checkout.viewLocalMenu')}
              </Button>
            );
          })()}

          {isDemoCheckout ? (
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-3 mt-1 border-t">
              <Button type="button" variant="outline" className="flex-1 min-w-[140px]" asChild>
                <Link to="/register">Crear mi menú</Link>
              </Button>
              <Button
                type="button"
                className="flex-1 min-w-[140px] bg-violet-600 hover:bg-violet-600/90"
                asChild
              >
                <Link to="/pricing">Ver planes</Link>
              </Button>
              <Button type="button" variant="secondary" className="flex-1 min-w-[140px]" asChild>
                <Link to={getPublicMenuPath(routeBid, 'assistant')}>Probar asistente</Link>
              </Button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
